import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Grid
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CountrySelect, StateSelect, CitySelect } from '../../components';
import { addUpdateOrderAddressAction, fetchOrderAddressAction } from '../../store/new-order/asyncActions';
import Joi from "joi";

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

export function BillingShippingAddress({ open, onClose, user, order_id, customerAddresses, currentAddresses, onAddressChange }) {
  const dispatch = useDispatch();

  const addUpdateOrderAddressData = useSelector((state) => state?.newOrder?.addUpdateOrderAddress);
  const fetchOrderAddressData = useSelector((state) => state?.newOrder?.fetchOrderAddress);
  const { countries, states, cities } = useSelector((state) => state?.location || {});

  // Helper function to find full object by ID
  const findCountryById = (countryId) => {
    if (!countryId) return '';
    // First try from customerAddresses
    const fromCustomer = customerAddresses?.addresses?.billing?.country_id?.country_id === countryId 
      ? customerAddresses.addresses.billing.country_id 
      : (customerAddresses?.addresses?.shipping?.country_id?.country_id === countryId 
        ? customerAddresses.addresses.shipping.country_id 
        : null);
    if (fromCustomer) return fromCustomer;
    // Then try from Redux store
    const fromStore = countries?.data?.data?.find(c => c.country_id === countryId);
    return fromStore || (countryId ? { country_id: countryId } : '');
  };

  const findStateById = (stateId) => {
    if (!stateId) return '';
    // First try from customerAddresses
    const fromCustomer = customerAddresses?.addresses?.billing?.state_id?.state_id === stateId 
      ? customerAddresses.addresses.billing.state_id 
      : (customerAddresses?.addresses?.shipping?.state_id?.state_id === stateId 
        ? customerAddresses.addresses.shipping.state_id 
        : null);
    if (fromCustomer) return fromCustomer;
    // Then try from Redux store
    const fromStore = states?.data?.data?.find(s => s.state_id === stateId);
    return fromStore || (stateId ? { state_id: stateId } : '');
  };

  const findCityById = (cityId) => {
    if (!cityId) return '';
    // First try from customerAddresses
    const fromCustomer = customerAddresses?.addresses?.billing?.city?.city_id === cityId 
      ? customerAddresses.addresses.billing.city 
      : (customerAddresses?.addresses?.shipping?.city?.city_id === cityId 
        ? customerAddresses.addresses.shipping.city 
        : null);
    if (fromCustomer) return fromCustomer;
    // Then try from Redux store
    const fromStore = cities?.data?.data?.find(c => c.city_id === cityId);
    return fromStore || (cityId ? { city_id: cityId } : '');
  };

  const [billing, setBilling] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    country: '',
    state: '',
    city: '',
    gstin: '',
    mobile_no: ''
  });

  const [shipping, setShipping] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    country: '',
    state: '',
    city: '',
    contact_person: '',
    contact_number: ''
  });

  const [errors, setErrors] = useState({});
  const formInitializedRef = useRef(false);

  const billingSchema = Joi.object({
    name: Joi.string().required().label('Billing Name'),
    addressLine1: Joi.string().required().label('Billing Address Line 1'),
    addressLine2: Joi.string().allow('').optional().label('Billing Address Line 2'),
    pincode: Joi.string().allow('').optional().label('Billing Pincode'),
    country: Joi.any().optional().label('Billing Country'),
    state: Joi.any().optional().label('Billing State'),
    city: Joi.any().optional().label('Billing City'),
    gstin: Joi.string().allow('').optional().label('GSTIN'),
    mobile_no: Joi.string().allow('').optional().label('Mobile No')
  });

  const shippingSchema = Joi.object({
    name: Joi.string().required().label('Shipping Name'),
    addressLine1: Joi.string().required().label('Shipping Address Line 1'),
    addressLine2: Joi.string().allow('').optional().label('Shipping Address Line 2'),
    pincode: Joi.string().required().label('Shipping Pincode'),
    country: Joi.any()
      .required()
      .disallow(null)
      .label('Shipping Country')
      .messages({
        'any.required': 'Please select a shipping country.',
        'any.invalid': 'Shipping country cannot be empty.'
      }),
    state: Joi.any()
      .required()
      .disallow(null)
      .label('Shipping State')
      .messages({
        'any.required': 'Please select a shipping state.',
        'any.invalid': 'Shipping state cannot be empty.'
      }),
    city: Joi.any()
      .required()
      .disallow(null)
      .label('Shipping City')
      .messages({
        'any.required': 'Please select a shipping city.',
        'any.invalid': 'Shipping city cannot be empty.'
      }),
    contact_person: Joi.string().allow('').optional().label('Contact Person'),
    contact_number: Joi.string().allow('').optional().label('Contact Number')
  });

  useEffect(() => {
    if (open && order_id && order_id.trim() !== '') {
      // For edit order - fetch order address
      formInitializedRef.current = false;
      dispatch(fetchOrderAddressAction({
        auth_token: user?.auth_token,
        user_type: user?.user_type,
        user_id: user?.user_id,
        order_id: order_id
      }));
    } else if (open && !order_id) {
      formInitializedRef.current = false;
      // For new order - prioritize currentAddresses (modified data) over customerAddresses (original data)
      if (currentAddresses && typeof currentAddresses === 'object' && !Array.isArray(currentAddresses)) {
        // Use modified address data from orderForm (now an object, not array)
        const addr = currentAddresses;
        
        // Find full objects by ID using helper functions
        const billingCountry = findCountryById(addr?.billing_country_id);
        const billingState = findStateById(addr?.billing_state_id);
        const billingCity = findCityById(addr?.billing_city);
        
        const shippingCountry = findCountryById(addr?.delivery_country_id);
        const shippingState = findStateById(addr?.delivery_state_id);
        const shippingCity = findCityById(addr?.delivery_city);
        
        setBilling({
          name: addr?.billing_name || '',
          addressLine1: addr?.billing_addressline_1 || '',
          addressLine2: addr?.billing_addressline_2 || '',
          pincode: addr?.billing_pincode || '',
          country: billingCountry,
          state: billingState,
          city: billingCity,
          gstin: addr?.billing_gst_no || '',
          mobile_no: addr?.mobile_no || '',
        });
        setShipping({
          name: addr?.delivery_name || '',
          addressLine1: addr?.delivery_addressline_1 || '',
          addressLine2: addr?.delivery_addressline_2 || '',
          pincode: addr?.delivery_pincode || '',
          country: shippingCountry,
          state: shippingState,
          city: shippingCity,
          contact_person: addr?.contact_person || '',
          contact_number: addr?.contact_number || '',
        });
      } else if (customerAddresses) {
        // Fall back to original customer addresses
        const billing = customerAddresses?.addresses?.billing;
        const shipping = customerAddresses?.addresses?.shipping;
        if (billing) {
          setBilling({
            name: billing?.name || '',
            addressLine1: billing?.addressline_1 || '',
            addressLine2: billing?.addressline_2 || '',
            pincode: billing?.pincode || '',
            country: billing?.country_id || '',
            state: billing?.state_id || '',
            city: billing?.city || '',
            gstin: billing?.gst_no || '',
            mobile_no: billing?.mobile_no || '',
          });
        }
        if (shipping) {
          setShipping({
            name: shipping?.name || '',
            addressLine1: shipping?.addressline_1 || '',
            addressLine2: shipping?.addressline_2 || '',
            pincode: shipping?.pincode || '',
            country: shipping?.country_id || '',
            state: shipping?.state_id || '',
            city: shipping?.city || '',
            contact_person: shipping?.contact_person || '',
            contact_number: shipping?.contact_number || '',
          });
        }
      }
      formInitializedRef.current = true;
    } else if (!open) {
      // Reset when dialog closes
      formInitializedRef.current = false;
    }
  }, [open, order_id, user, customerAddresses, currentAddresses]);

  useEffect(() => {
    if (fetchOrderAddressData?.data?.success) {
      const addressData = fetchOrderAddressData.data.data;
      const billing = addressData?.addresses?.billing;
      const shipping = addressData?.addresses?.shipping;
      if (billing) {
        setBilling({
          name: billing?.name || '',
          addressLine1: billing?.addressline_1 || '',
          addressLine2: billing?.addressline_2 || '',
          pincode: billing?.pincode || '',
          country: billing?.country_id || '',
          state: billing?.state_id || '',
          city: billing?.city || '',
          gstin: billing?.gst_no || '',
          mobile_no: billing?.mobile_no || '',
        });
      }
      if (shipping) {
        setShipping({
          name: shipping?.name || '',
          addressLine1: shipping?.addressline_1 || '',
          addressLine2: shipping?.addressline_2 || '',
          pincode: shipping?.pincode || '',
          country: shipping?.country_id || '',
          state: shipping?.state_id || '',
          city: shipping?.city || '',
          contact_person: shipping?.contact_person || '',
          contact_number: shipping?.contact_number || '',
        });
      }
      fetchOrderAddressData.data = null; // Reset after use
    }
  }, [fetchOrderAddressData]);

  useEffect(() => {
    if (addUpdateOrderAddressData?.data?.success) {
      onClose(); // Close dialog on success
      addUpdateOrderAddressData.data = null; // Reset after use
    }
  }, [addUpdateOrderAddressData, onClose]);

  // Update form when location data loads (for proper display of country/state/city names)
  // Only update if form is initialized and we have minimal objects (missing names) - don't interfere with user selections
  useEffect(() => {
    if (open && !order_id && formInitializedRef.current && currentAddresses && typeof currentAddresses === 'object' && !Array.isArray(currentAddresses)) {
      const addr = currentAddresses;
      
      // Only update if location data is available and we have minimal objects
      const hasCountries = countries?.data?.data?.length > 0;
      const hasStates = states?.data?.data?.length > 0;
      const hasCities = cities?.data?.data?.length > 0;
      
      if (hasCountries || hasStates || hasCities) {
        setBilling(prev => {
          const updates = {};
          // Only update if current value is a minimal object (has ID but no name) and IDs match
          if (prev.country && typeof prev.country === 'object' && prev.country.country_id && !prev.country.country_name) {
            const fullCountry = findCountryById(addr?.billing_country_id);
            if (fullCountry && fullCountry.country_name && String(fullCountry.country_id) === String(prev.country.country_id)) {
              updates.country = fullCountry;
            }
          }
          if (prev.state && typeof prev.state === 'object' && prev.state.state_id && !prev.state.state_name) {
            const fullState = findStateById(addr?.billing_state_id);
            if (fullState && fullState.state_name && String(fullState.state_id) === String(prev.state.state_id)) {
              updates.state = fullState;
            }
          }
          if (prev.city && typeof prev.city === 'object' && prev.city.city_id && !prev.city.city_name) {
            const fullCity = findCityById(addr?.billing_city);
            if (fullCity && fullCity.city_name && String(fullCity.city_id) === String(prev.city.city_id)) {
              updates.city = fullCity;
            }
          }
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
        
        setShipping(prev => {
          const updates = {};
          // Only update if current value is a minimal object (has ID but no name) and IDs match
          if (prev.country && typeof prev.country === 'object' && prev.country.country_id && !prev.country.country_name) {
            const fullCountry = findCountryById(addr?.delivery_country_id);
            if (fullCountry && fullCountry.country_name && String(fullCountry.country_id) === String(prev.country.country_id)) {
              updates.country = fullCountry;
            }
          }
          if (prev.state && typeof prev.state === 'object' && prev.state.state_id && !prev.state.state_name) {
            const fullState = findStateById(addr?.delivery_state_id);
            if (fullState && fullState.state_name && String(fullState.state_id) === String(prev.state.state_id)) {
              updates.state = fullState;
            }
          }
          if (prev.city && typeof prev.city === 'object' && prev.city.city_id && !prev.city.city_name) {
            const fullCity = findCityById(addr?.delivery_city);
            if (fullCity && fullCity.city_name && String(fullCity.city_id) === String(prev.city.city_id)) {
              updates.city = fullCity;
            }
          }
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
      }
    }
  }, [countries?.data?.data, states?.data?.data, cities?.data?.data, open, order_id, currentAddresses]);

  const validate = () => {
    const billingValidation = billingSchema.validate(billing, { abortEarly: false });
    const shippingValidation = shippingSchema.validate(shipping, { abortEarly: false });

    const billingErrors = {};
    const shippingErrors = {};

    if (billingValidation.error) {
      billingValidation.error.details.forEach((detail) => {
        billingErrors[detail.path[0]] = detail.message;
      });
    }

    if (shippingValidation.error) {
      shippingValidation.error.details.forEach((detail) => {
        shippingErrors[detail.path[0]] = detail.message;
      });
    }

    setErrors({ billing: billingErrors, shipping: shippingErrors });
    return Object.keys(billingErrors).length === 0 && Object.keys(shippingErrors).length === 0;
  };

  const handleBillingChange = (e) => {
    setBilling({ ...billing, [e.target.name]: e.target.value });
    if (errors.billing && errors.billing[e.target.name]) {
      setErrors({
        ...errors,
        billing: { ...errors.billing, [e.target.name]: null }
      });
    }
  };

  const handleShippingChange = (e) => {
    setShipping({ ...shipping, [e.target.name]: e.target.value });
    if (errors.shipping && errors.shipping[e.target.name]) {
      setErrors({
        ...errors,
        shipping: { ...errors.shipping, [e.target.name]: null }
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const transformedAddress = {
        mobile_no: billing?.mobile_no || '',
        billing_name: billing?.name || '',
        billing_addressline_1: billing?.addressLine1 || '',
        billing_addressline_2: billing?.addressLine2 || '',
        billing_gst_no: billing?.gstin || '',
        billing_country_id: billing?.country?.country_id || '',
        billing_state_id: billing?.state?.state_id || '',
        billing_city: billing?.city?.city_id || '',
        billing_pincode: billing?.pincode || '',
        delivery_name: shipping?.name || '',
        contact_person: shipping?.contact_person || '',
        contact_number: shipping?.contact_number || '',
        delivery_addressline_1: shipping?.addressLine1 || '',
        delivery_addressline_2: shipping?.addressLine2 || '',
        delivery_country_id: shipping?.country?.country_id || '',
        delivery_state_id: shipping?.state?.state_id || '',
        delivery_city: shipping?.city?.city_id || '',
        delivery_pincode: shipping?.pincode || '',
      };

      if (order_id && order_id.trim() !== '') {
        // For edit order - update via API
        const addressData = {
          auth_token: user?.auth_token,
          user_type: user?.user_type,
          user_id: user?.user_id,
          order_id: order_id,
          ...transformedAddress,
        };
        dispatch(addUpdateOrderAddressAction(addressData));
      } else {
        // For new order - update parent component
        if (onAddressChange) {
          onAddressChange(transformedAddress);
        }
        onClose(); // Close dialog
      }
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Billing & Shipping Address
          </Typography>
          <IconButton
            className='ml_auto px-0'
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogTitle></DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} style={{ marginTop: '35px' }}>
          <Grid container>
            {/* Billing Address Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Billing Address
              </Typography>
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Name"
                name="name"
                value={billing.name}
                onChange={handleBillingChange}
                margin="normal"
                required
                error={!!errors.billing?.name}
                helperText={errors.billing?.name}
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Address Line 1"
                name="addressLine1"
                value={billing.addressLine1}
                onChange={handleBillingChange}
                margin="normal"
                required
                error={!!errors.billing?.addressLine1}
                helperText={errors.billing?.addressLine1}
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Address Line 2"
                name="addressLine2"
                value={billing.addressLine2}
                onChange={handleBillingChange}
                margin="normal"
                error={!!errors.billing?.addressLine2}
                helperText={errors.billing?.addressLine2}
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Pincode"
                name="pincode"
                value={billing.pincode}
                onChange={handleBillingChange}
                margin="normal"
                error={!!errors.billing?.pincode}
                helperText={errors.billing?.pincode}
              />
              <Box style={{ paddingTop: '12px', paddingBottom: '10px' }}>
                <CountrySelect
                  className="login_form_field"
                  value={billing.country}
                  onChange={(country) => setBilling({ ...billing, country: country || null })}
                  error={false}
                  helperText=""
                  user={user}
                />
              </Box>
              <Box style={{ paddingTop: '12px', paddingBottom: '10px' }}>
                <StateSelect
                  className="login_form_field"
                  country={billing.country}
                  value={billing.state}
                  onChange={(state) => setBilling({ ...billing, state: state || null })}
                  error={false}
                  helperText=""
                  user={user}
                />
              </Box>
              <Box style={{ paddingTop: '12px', paddingBottom: '8px' }}>
                <CitySelect
                  className="login_form_field"
                  state={billing.state}
                  value={billing.city}
                  onChange={(city) => setBilling({ ...billing, city: city || null })}
                  error={false}
                  helperText=""
                  user={user}
                />
              </Box>
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="GSTIN"
                name="gstin"
                value={billing.gstin}
                onChange={handleBillingChange}
                margin="normal"
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Mobile Number"
                name="mobile_no"
                value={billing.mobile_no}
                onChange={handleBillingChange}
                margin="normal"
                error={!!errors.billing?.mobile_no}
                helperText={errors.billing?.mobile_no}
              />
            </Grid>

            {/* Shipping Address Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom style={{ marginTop: '25px' }}>
                Shipping Address
              </Typography>
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Name"
                name="name"
                value={shipping.name}
                onChange={handleShippingChange}
                margin="normal"
                required
                error={!!errors.shipping?.name}
                helperText={errors.shipping?.name}
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Address Line 1"
                name="addressLine1"
                value={shipping.addressLine1}
                onChange={handleShippingChange}
                margin="normal"
                required
                error={!!errors.shipping?.addressLine1}
                helperText={errors.shipping?.addressLine1}
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Address Line 2"
                name="addressLine2"
                value={shipping.addressLine2}
                onChange={handleShippingChange}
                margin="normal"
                error={!!errors.shipping?.addressLine2}
                helperText={errors.shipping?.addressLine2}
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Pincode"
                name="pincode"
                value={shipping.pincode}
                onChange={handleShippingChange}
                margin="normal"
                required
                error={!!errors.shipping?.pincode}
                helperText={errors.shipping?.pincode}
              />
              <Box style={{ paddingTop: '12px', paddingBottom: '10px' }}>
                <CountrySelect
                  className="login_form_field"
                  value={shipping.country}
                  onChange={(country) => setShipping({ ...shipping, country: country || null })}
                  user={user}
                  required
                  error={!!errors.shipping?.country}
                  helperText={errors.shipping?.country}
                />
              </Box>
              <Box style={{ paddingTop: '12px', paddingBottom: '10px' }}>
                <StateSelect
                  className="login_form_field"
                  country={shipping.country}
                  value={shipping.state}
                  onChange={(state) => setShipping({ ...shipping, state: state || null })}
                  user={user}
                  required
                  error={!!errors.shipping?.state}
                  helperText={errors.shipping?.state}
                />
              </Box>
              <Box style={{ paddingTop: '12px', paddingBottom: '8px' }}>
                <CitySelect
                  className="login_form_field"
                  state={shipping.state}
                  value={shipping.city}
                  onChange={(city) => setShipping({ ...shipping, city: city || null })}
                  user={user}
                  required
                  error={!!errors.shipping?.city}
                  helperText={errors.shipping?.city}
                />
              </Box>
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Contact Person Name"
                name="contact_person"
                value={shipping.contact_person}
                onChange={handleShippingChange}
                margin="normal"
                error={!!errors.shipping?.contact_person}
                helperText={errors.shipping?.contact_person}
              />
              <TextField
                className="login_form_field"
                variant="outlined"
                size="small"
                fullWidth
                label="Contact Mobile Number"
                name="contact_number"
                value={shipping.contact_number}
                onChange={handleShippingChange}
                margin="normal"
                error={!!errors.shipping?.contact_number}
                helperText={errors.shipping?.contact_number}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button fullWidth type="submit" color="primary" variant="contained" onClick={handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
